import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useReducer
} from 'react';

import { reportPaymentError, type WidgetTelemetryContext } from '../core/logs';
import { PaymentError } from '../core/payment-error';

interface PaymentErrorState {
    errors: PaymentError[];
    isErrorBoundaryActive: boolean;
    isDismissed: boolean;
}

type PaymentErrorAction =
    | { type: 'ADD_ERROR'; error: PaymentError }
    | { type: 'REMOVE_ERROR'; traceId: string }
    | { type: 'CLEAR_ERRORS' }
    | { type: 'DISMISS_ALL' }
    | { type: 'ACTIVATE_ERROR_BOUNDARY' }
    | { type: 'DEACTIVATE_ERROR_BOUNDARY' };

const initialState: PaymentErrorState = {
    errors: [],
    isErrorBoundaryActive: false,
    isDismissed: false
};

const paymentErrorReducer = (
    state: PaymentErrorState,
    action: PaymentErrorAction
): PaymentErrorState => {
    switch (action.type) {
        case 'ADD_ERROR':
            return {
                ...state,
                errors: [...state.errors, action.error],
                isDismissed: false
            };
        case 'REMOVE_ERROR':
            return {
                ...state,
                errors: state.errors.filter(
                    (error) => error.traceId !== action.traceId
                )
            };
        case 'CLEAR_ERRORS':
            if (state.errors.length === 0 && !state.isDismissed) {
                return state;
            }

            return {
                ...state,
                errors: [],
                isDismissed: false
            };
        case 'DISMISS_ALL':
            return {
                ...state,
                isDismissed: true
            };
        case 'ACTIVATE_ERROR_BOUNDARY':
            return {
                ...state,
                isErrorBoundaryActive: true
            };
        case 'DEACTIVATE_ERROR_BOUNDARY':
            return {
                ...state,
                isErrorBoundaryActive: false
            };
        default:
            return state;
    }
};

interface PaymentErrorContextValue {
    state: PaymentErrorState;
    addError: (error: unknown) => void;
    reportError: (error: unknown) => PaymentError;
    removeError: (traceId: string) => void;
    clearErrors: () => void;
    dismissAllErrors: () => void;
    activateErrorBoundary: () => void;
    deactivateErrorBoundary: () => void;
    hasExternalErrorHandler: boolean;
}

const PaymentErrorContext = createContext<PaymentErrorContextValue | undefined>(
    undefined
);

interface PaymentErrorProviderProps {
    children: ReactNode;
    onError?: (error: PaymentError) => void;
    telemetryContext?: WidgetTelemetryContext;
}

export const PaymentErrorProvider: React.FC<PaymentErrorProviderProps> = ({
    children,
    onError,
    telemetryContext
}) => {
    const [state, dispatch] = useReducer(paymentErrorReducer, initialState);

    const hasExternalErrorHandler = !!onError;

    const reportError = useCallback(
        (error: unknown) => {
            const paymentError = PaymentError.fromUnknown(error);
            reportPaymentError(paymentError, telemetryContext);
            onError?.(paymentError);
            return paymentError;
        },
        [onError, telemetryContext]
    );

    const addError = useCallback(
        (error: unknown) => {
            dispatch({ type: 'ADD_ERROR', error: reportError(error) });
        },
        [reportError]
    );

    const removeError = useCallback((traceId: string) => {
        dispatch({ type: 'REMOVE_ERROR', traceId });
    }, []);

    const clearErrors = useCallback(() => {
        dispatch({ type: 'CLEAR_ERRORS' });
    }, []);

    const dismissAllErrors = useCallback(() => {
        dispatch({ type: 'DISMISS_ALL' });
    }, []);

    const activateErrorBoundary = useCallback(() => {
        dispatch({ type: 'ACTIVATE_ERROR_BOUNDARY' });
    }, []);

    const deactivateErrorBoundary = useCallback(() => {
        dispatch({ type: 'DEACTIVATE_ERROR_BOUNDARY' });
    }, []);

    const value = {
        state,
        addError,
        reportError,
        removeError,
        clearErrors,
        dismissAllErrors,
        activateErrorBoundary,
        deactivateErrorBoundary,
        hasExternalErrorHandler
    };

    return (
        <PaymentErrorContext.Provider value={value}>
            {children}
        </PaymentErrorContext.Provider>
    );
};

export const usePaymentErrorContext = (): PaymentErrorContextValue => {
    const context = useContext(PaymentErrorContext);
    if (!context) {
        throw new PaymentError(
            'usePaymentErrorContext must be used within a PaymentErrorProvider',
            'VALIDATION_ERROR'
        );
    }
    return context;
};
